import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getCompletenessItems } from 'partner360/entities/partnerCompleteness'
import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { getOfferingsInfo } from 'partner360/helpers/offerings/getOfferingsInfo'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import { getCockpitPlanningStatus, useCockpitPlanningStatuses } from '../../../../model/hooks'
import {
  selectCockpitFirstPartnerOfferingsCatalog,
  selectCockpitOffering,
  selectCockpitPartnerCompleteness,
  selectCockpitPartnerData,
} from '../../../../model/selectors'

import styles from './AttentionCompletenessOverview.module.scss'

/* The programmes' own bars on `Overall` take the cockpit accent at the sections' light tint. */
const OFFERING_FILL = 'rgba(var(--color-primary-light--rgb), 0.2)'

/* Truncated rather than rounded, as the completeness detail does: 99.8% is not a finished plan. */
const toPercent = (value: number | undefined) => Math.floor((value ?? 0) * 100)

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.completeness
const STATUS_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planningStatus

export interface ICompletenessOverviewRow {
  id: string
  label: string
  value: number | undefined
  fill: string
}

/**
 * The rows of a clean plan: the completeness of every section of the programme in view, or of
 * every programme on `Overall`. Empty when the payload measures none.
 */
export const useCompletenessOverviewRows = (): ICompletenessOverviewRow[] => {
  const selectedOffering = useSelector(selectCockpitOffering)
  const completenessProfiles = useSelector(selectCockpitPartnerCompleteness)
  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const { isDistributor } = useSelector(selectPartnerAccess)

  return useMemo(() => {
    if (selectedOffering === ProgramTypes.Overall) {
      return getOfferingsInfo(getAvailableProgramTypes(offeringsCatalog)).enabledOfferings.map(
        ({ programType }) => ({
          id: programType,
          label: getOfferingTitleKey(programType),
          value: completenessProfiles.find(({ profile }) => profile === programType)?.total,
          fill: OFFERING_FILL,
        })
      )
    }

    return getCompletenessItems(
      completenessProfiles.find(({ profile }) => profile === selectedOffering),
      isDistributor
    ).map(({ id, label, data, color }) => ({ id, label, value: data, fill: color }))
  }, [completenessProfiles, isDistributor, offeringsCatalog, selectedOffering])
}

interface IAttentionCompletenessOverviewProps {
  rows: ICompletenessOverviewRow[]
}

/**
 * What the tile shows in place of `Nothing needs attention` once the plan is clean: a section laid
 * out like a status detail — its title, the plan's total and signature on one line, and the rows
 * the completeness detail draws, each bar filled behind its name and number.
 */
export const AttentionCompletenessOverview = ({ rows }: IAttentionCompletenessOverviewProps) => {
  const selectedOffering = useSelector(selectCockpitOffering)
  const partnerData = useSelector(selectCockpitPartnerData)
  const status = getCockpitPlanningStatus(
    useCockpitPlanningStatuses(partnerData[0]?.PartnerId ?? ''),
    selectedOffering
  )
  const { completeness, acknowledgment } = status

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <span className={styles.title}>
          <Translation text={KEYS.overviewTitle} span />
        </span>

        <span className={styles.summary}>
          <span className={styles.total}>
            {'percent' in completeness ? (
              `${completeness.percent ?? 0}%`
            ) : (
              <Translation
                text={STATUS_KEYS.count}
                replaceParams={[
                  { key: '{count}', value: String(completeness.count) },
                  { key: '{total}', value: String(completeness.total) },
                ]}
                span
              />
            )}
          </span>
          <Translation text={STATUS_KEYS.complete} span />
          {'acknowledged' in acknowledgment && acknowledgment.acknowledged && (
            <>
              <span aria-hidden="true">·</span>
              <Translation
                text={STATUS_KEYS.acknowledgedOn}
                replaceParams={[{ key: '{date}', value: acknowledgment.date ?? '--' }]}
                span
              />
            </>
          )}
          {'count' in acknowledgment && (
            <>
              <span aria-hidden="true">·</span>
              <Translation
                text={STATUS_KEYS.acknowledgedCount}
                replaceParams={[
                  { key: '{count}', value: String(acknowledgment.count) },
                  { key: '{total}', value: String(acknowledgment.total) },
                ]}
                span
              />
            </>
          )}
        </span>
      </div>

      <div className={styles.rows}>
        {rows.map(({ id, label, value, fill }) => (
          <div key={id} className={styles.row}>
            <span
              className={styles.fill}
              style={{ transform: `scaleX(${Math.min(1, value ?? 0)})`, background: fill }}
              aria-hidden
            />
            <span className={styles.label}>
              <Translation text={label} span />
            </span>
            <span className={styles.value}>{`${toPercent(value)}%`}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

AttentionCompletenessOverview.displayName = 'AttentionCompletenessOverview'
